/**
 * TrustGrid Connectors Page
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Modal, Input, Select, Tag, Spin, Button, Tooltip, Switch, Progress } from 'antd';
import { PlusOutlined, PlayCircleOutlined, DeleteOutlined, EditOutlined, CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined, CopyOutlined, ExclamationCircleOutlined, SyncOutlined, LoadingOutlined } from '@ant-design/icons';
import { createConnector, listConnectors, listAllConnectors, getConnector, updateConnector, testConnector, syncConnector, deleteConnector, getAllConnectorsSyncStatus } from '../api/trustgridApi';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import ProjectSelector from './ProjectSelector';
import CopyConnectorModal from './CopyConnectorModal';
import { showError, showSuccess } from '../../../utils/notifications';
import { handleCrossOrgError } from '../utils/errorHandlers';

const { Option } = Select;

function Connectors() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedProjectId, isAllProjects, selectProject } = useTrustGrid();

  // For operations that require a project ID (create, edit, delete), use selectedProjectId
  // When in "All Projects" view, we'll get project_id from each connector
  const projectId = selectedProjectId;

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Connectors', path: '/trustgrid/connectors' }];
  }, [location.state]);

  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editConnectorId, setEditConnectorId] = useState(null);
  const [connectorType, setConnectorType] = useState('github');
  const [connectorName, setConnectorName] = useState('');
  const [connectorConfig, setConnectorConfig] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [connectorsToCopy, setConnectorsToCopy] = useState([]);
  const [initialSync, setInitialSync] = useState(false);
  const [syncingConnectors, setSyncingConnectors] = useState(new Set());

  useEffect(() => {
    setSelectedRowKeys([]);  // Clear stale selections from previous project
    fetchConnectors();
  }, [selectedProjectId, isAllProjects]);

  // Poll sync status when connectors are syncing
  // Only works for single project view (not "All Projects")
  useEffect(() => {
    if (isAllProjects || !selectedProjectId || syncingConnectors.size === 0) return;

    const pollSyncStatus = async () => {
      try {
        const response = await getAllConnectorsSyncStatus(selectedProjectId);

        // Update connectors list
        setConnectors(response.data || []);

        // Update syncing set
        const stillSyncing = new Set();
        (response.data || []).forEach(c => {
          if (c.sync_status === 'syncing') {
            stillSyncing.add(c.id);
          }
        });

        if (stillSyncing.size !== syncingConnectors.size) {
          setSyncingConnectors(stillSyncing);
        }
      } catch (error) {
        console.error('Failed to fetch sync status:', error);
      }
    };

    // Poll immediately when syncing starts
    pollSyncStatus();

    // Then continue polling every 2 seconds
    const interval = setInterval(pollSyncStatus, 2000);

    return () => clearInterval(interval);
  }, [syncingConnectors.size, selectedProjectId, isAllProjects]);

  const fetchConnectors = async () => {
    setLoading(true);
    try {
      let res;
      if (isAllProjects) {
        // Fetch connectors across all user's projects
        res = await listAllConnectors();
      } else if (selectedProjectId) {
        // Fetch connectors for specific project
        res = await listConnectors(selectedProjectId);
      } else {
        // No project selected yet
        setConnectors([]);
        setLoading(false);
        return;
      }
      setConnectors(res.data.connectors || []);
    } catch (error) {
      // SECURITY: Handle cross-org access silently (404 = project doesn't belong to user)
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Connectors');
      if (!wasHandled) {
        // Show error for other cases (network, server errors, etc.)
        console.error('Failed to fetch connectors:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setConnectorType('github');
    setConnectorName('');
    setConnectorConfig({});
    setEditMode(false);
    setEditConnectorId(null);
    setInitialSync(false);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleEdit = async (connectorId) => {
    try {
      // Find connector in list to get its project_id
      const connectorInList = connectors.find(c => c.id === connectorId);
      const connectorProjectId = connectorInList?.project_id || selectedProjectId;

      const res = await getConnector(connectorProjectId, connectorId);
      const connector = res.data.connector;

      // Pre-populate form with existing values
      setConnectorType(connector.connector_type);
      setConnectorName(connector.name);
      setConnectorConfig(connector.config);
      setEditMode(true);
      setEditConnectorId(connectorId);
      setOpenDialog(true);
    } catch (error) {
      console.error('Failed to fetch connector:', error);
      showError(error);
    }
  };

  const handleCreate = async () => {
    // Client-side validation
    if (!connectorName || connectorName.trim() === '') {
      showError('Please enter a connector name');
      return;
    }

    // Validate config has required fields based on connector type
    const requiredFields = getRequiredFields(connectorType);
    const missingFields = requiredFields.filter(field => !connectorConfig[field]);

    if (missingFields.length > 0) {
      showError(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      if (editMode) {
        // Update existing connector
        await updateConnector(projectId, editConnectorId, {
          connector_type: connectorType,
          name: connectorName,
          config: connectorConfig
        });
        showSuccess('Connector updated');
      } else {
        // Create new connector
        const response = await createConnector(projectId, {
          connector_type: connectorType,
          name: connectorName,
          config: connectorConfig
        });
        const newConnectorId = response.data.connector.id;

        // Trigger initial sync if enabled
        if (initialSync) {
          try {
            await syncConnector(newConnectorId);

            // Mark connector as syncing to start polling
            setSyncingConnectors(prev => new Set([...prev, newConnectorId]));

            showSuccess('Connector created and sync initiated');
          } catch (syncError) {
            showSuccess('Connector created but sync failed to start');
            console.error('Sync error:', syncError);
          }
        } else {
          showSuccess('Connector created');
        }
      }
      handleDialogClose();
      fetchConnectors();
    } catch (error) {
      console.error(`Failed to ${editMode ? 'update' : 'create'} connector:`, error);
      showError(error);
    }
  };

  const getRequiredFields = (type) => {
    const fieldMap = {
      'github': ['token'],  // org is optional - can use personal repos
      's3': ['aws_access_key', 'aws_secret_key', 'bucket_name', 'region'],
      'datadog': ['api_key', 'app_key'],
      'aws_cloudtrail': ['aws_access_key', 'aws_secret_key', 'region'],
      'aws_config': ['aws_access_key', 'aws_secret_key', 'region'],
      'jira': ['site_url', 'email', 'api_token'],
      'slack': ['bot_token', 'signing_secret'],
      'mlflow': ['tracking_uri'],
      'openai': ['api_key'],
      'bedrock': ['aws_access_key', 'aws_secret_key', 'region']
    };
    return fieldMap[type] || [];
  };

  const handleTest = async (connectorId) => {
    try {
      const res = await testConnector(projectId, connectorId);
      showSuccess(res.data.message || 'Connection test successful');
    } catch (error) {
      console.error('Failed to test connector:', error);
      showError(error);
    }
  };

  const handleSync = async (connectorId) => {
    try {
      const res = await syncConnector(connectorId);

      // Mark connector as syncing to start polling (polling will fetch immediately)
      setSyncingConnectors(prev => new Set([...prev, connectorId]));

      showSuccess(res.data.message || 'Sync initiated');
    } catch (error) {
      console.error('Failed to sync connector:', error);
      showError(error);
    }
  };

  const handleDelete = async (connectorId) => {
    Modal.confirm({
      title: 'Delete Connector',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this connector? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteConnector(projectId, connectorId);
          showSuccess('Connector deleted');
          fetchConnectors();
        } catch (error) {
          console.error('Failed to delete connector:', error);
          showError(error);
        }
      }
    });
  };

  const handleBulkDelete = () => {
    const selectedConnectors = connectors.filter(c => selectedRowKeys.includes(c.id));
    Modal.confirm({
      title: `Delete ${selectedRowKeys.length} Connector${selectedRowKeys.length > 1 ? 's' : ''}`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to delete these connectors? This action cannot be undone.</p>
          <ul style={{ marginTop: '8px' }}>
            {selectedConnectors.map(c => (
              <li key={c.id}><strong>{c.name}</strong> ({c.connector_type})</li>
            ))}
          </ul>
        </div>
      ),
      okText: 'Delete All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await Promise.all(selectedRowKeys.map(id => deleteConnector(projectId, id)));
          showSuccess(`Deleted ${selectedRowKeys.length} connector${selectedRowKeys.length > 1 ? 's' : ''}`);
          setSelectedRowKeys([]);
          fetchConnectors();
        } catch (error) {
          console.error('Failed to delete connectors:', error);
          showError(error);
        }
      }
    });
  };

  const handleBulkTest = async () => {
    Modal.confirm({
      title: `Test ${selectedRowKeys.length} Connector${selectedRowKeys.length > 1 ? 's' : ''}`,
      icon: <PlayCircleOutlined />,
      content: `This will test the connection for ${selectedRowKeys.length} selected connector${selectedRowKeys.length > 1 ? 's' : ''}. Continue?`,
      okText: 'Test All',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await Promise.all(selectedRowKeys.map(id => testConnector(projectId, id)));
          showSuccess(`Tested ${selectedRowKeys.length} connector${selectedRowKeys.length > 1 ? 's' : ''}`);
          fetchConnectors();
        } catch (error) {
          console.error('Failed to test connectors:', error);
          showError(error);
        }
      }
    });
  };

  const handleBulkSync = async () => {
    Modal.confirm({
      title: `Sync ${selectedRowKeys.length} Connector${selectedRowKeys.length > 1 ? 's' : ''}`,
      icon: <SyncOutlined />,
      content: `This will initiate sync for ${selectedRowKeys.length} selected connector${selectedRowKeys.length > 1 ? 's' : ''}. Continue?`,
      okText: 'Sync All',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await Promise.all(selectedRowKeys.map(id => syncConnector(id)));

          // Mark all selected connectors as syncing to start polling (polling will fetch immediately)
          setSyncingConnectors(new Set(selectedRowKeys));

          showSuccess(`Syncing ${selectedRowKeys.length} connector${selectedRowKeys.length > 1 ? 's' : ''}`);
        } catch (error) {
          console.error('Failed to sync connectors:', error);
          showError(error);
        }
      }
    });
  };

  const handleCopySingle = (connector) => {
    // Get full connector config first
    getConnector(projectId, connector.id).then(res => {
      setConnectorsToCopy([res.data.connector]);
      setCopyModalOpen(true);
    }).catch(error => {
      console.error('Failed to fetch connector:', error);
      showError(error);
    });
  };

  const handleBulkCopy = () => {
    // Get full configs for selected connectors
    const fetchPromises = selectedRowKeys.map(id => getConnector(projectId, id));
    Promise.all(fetchPromises).then(responses => {
      const fullConnectors = responses.map(res => res.data.connector);
      setConnectorsToCopy(fullConnectors);
      setCopyModalOpen(true);
    }).catch(error => {
      console.error('Failed to fetch connectors:', error);
      showError(error);
    });
  };

  const handleCopySuccess = () => {
    setSelectedRowKeys([]);
    fetchConnectors();
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'connector_type',
      key: 'connector_type',
      width: 120
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 150,
      render: (projectName) => <Tag color="purple">{projectName}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const color = status === 'active' ? 'green' : 'default';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: 'Connection Health',
      key: 'connection_health',
      width: 150,
      render: (_, record) => {
        if (!record.last_tested_at) {
          return (
            <Tooltip title="Not tested">
              <MinusCircleOutlined style={{ fontSize: '18px', color: '#d9d9d9' }} />
              <span style={{ marginLeft: '8px', color: '#999' }}>Not tested</span>
            </Tooltip>
          );
        }

        const testResult = record.last_test_result || {};
        const isSuccess = testResult.success === true;

        if (isSuccess) {
          return (
            <Tooltip title={`Tested: ${new Date(record.last_tested_at).toLocaleString()}`}>
              <CheckCircleOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
              <span style={{ marginLeft: '8px', color: '#52c41a' }}>Healthy</span>
            </Tooltip>
          );
        } else {
          return (
            <Tooltip title={`Error: ${testResult.message || 'Connection failed'}\nTested: ${new Date(record.last_tested_at).toLocaleString()}`}>
              <CloseCircleOutlined style={{ fontSize: '18px', color: '#ff4d4f' }} />
              <span style={{ marginLeft: '8px', color: '#ff4d4f' }}>Failed</span>
            </Tooltip>
          );
        }
      }
    },
    {
      title: 'Last Tested',
      dataIndex: 'last_tested_at',
      key: 'last_tested_at',
      width: 150,
      render: (timestamp) => timestamp ? new Date(timestamp).toLocaleString() : '-'
    },
    {
      title: 'Sync Status',
      key: 'sync_status',
      width: 250,
      render: (_, record) => {
        const { sync_status, sync_progress_pct, message, last_synced_at, sync_error } = record;

        if (sync_status === 'syncing') {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LoadingOutlined spin style={{ fontSize: '14px', color: '#1890ff' }} />
              <span style={{ color: '#1890ff', fontSize: '12px' }}>
                {message || 'Syncing...'}
              </span>
            </div>
          );
        }

        if (sync_status === 'success') {
          const timeAgo = last_synced_at
            ? Math.floor((Date.now() - new Date(last_synced_at)) / 60000)
            : null;
          const timeText = timeAgo !== null
            ? (timeAgo < 1 ? 'just now' : timeAgo < 60 ? `${timeAgo}m ago` : `${Math.floor(timeAgo / 60)}h ago`)
            : 'Success';

          return (
            <Tooltip title={`Last synced: ${last_synced_at ? new Date(last_synced_at).toLocaleString() : 'N/A'}`}>
              <Tag color="success" icon={<CheckCircleOutlined />}>
                {timeText}
              </Tag>
            </Tooltip>
          );
        }

        if (sync_status === 'failed') {
          return (
            <Tooltip title={`Error: ${sync_error || message || 'Sync failed'}`}>
              <Tag color="error" icon={<CloseCircleOutlined />}>
                Failed
              </Tag>
            </Tooltip>
          );
        }

        return (
          <Tooltip title="Never synced">
            <Tag icon={<MinusCircleOutlined />}>
              Never synced
            </Tag>
          </Tooltip>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record.id)}
            />
          </Tooltip>
          <Tooltip title="Copy to Project">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopySingle(record)}
            />
          </Tooltip>
          <Tooltip title="Test Connection">
            <Button
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleTest(record.id)}
            />
          </Tooltip>
          <Tooltip title="Sync Now">
            <Button
              size="small"
              icon={<SyncOutlined />}
              onClick={() => handleSync(record.id)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
              danger
            />
          </Tooltip>
        </div>
      )
    }
  ];

  return (
    <TrustGridPage
      title="Connectors"
      subtitle="Configure data source connectors and manage sync operations"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchConnectors}
      actions={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpenDialog(true)}>
          Add Connector
        </Button>
      }
    >
      <ProjectSelector />

      {selectedRowKeys.length > 0 && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          background: '#e6f7ff',
          border: '1px solid #91d5ff',
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontWeight: 'bold' }}>
            {selectedRowKeys.length} connector{selectedRowKeys.length > 1 ? 's' : ''} selected
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button icon={<CopyOutlined />} onClick={handleBulkCopy}>
              Copy to Project
            </Button>
            <Button icon={<PlayCircleOutlined />} onClick={handleBulkTest}>
              Test Selected
            </Button>
            <Button icon={<SyncOutlined />} onClick={handleBulkSync}>
              Sync Selected
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBulkDelete}
            >
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          dataSource={connectors}
          columns={columns}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true
          }}
          bordered
        />
      )}

      <Modal
        title={editMode ? "Edit Connector" : "Add Connector"}
        open={openDialog}
        onCancel={handleDialogClose}
        footer={[
          <Button key="cancel" onClick={handleDialogClose}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleCreate}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Type</label>
          <Select
            value={connectorType}
            onChange={setConnectorType}
            style={{ width: '100%', marginBottom: '16px' }}
          >
            <Option value="github">GitHub</Option>
            <Option value="s3">S3 Metadata Catalog</Option>
            <Option value="datadog">Datadog</Option>
            <Option value="aws_cloudtrail">AWS CloudTrail</Option>
            <Option value="aws_config">AWS Config</Option>
            <Option value="jira">Jira Cloud</Option>
            <Option value="slack">Slack</Option>
            <Option value="mlflow">MLflow</Option>
            <Option value="openai">OpenAI</Option>
            <Option value="bedrock">AWS Bedrock</Option>
          </Select>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Name</label>
          <Input
            value={connectorName}
            onChange={(e) => setConnectorName(e.target.value)}
            placeholder="Enter connector name"
          />
        </div>
        {connectorType === 'github' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Token</label>
              <Input.Password
                value={connectorConfig.token || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, token: e.target.value })}
                placeholder="GitHub Personal Access Token"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Organization</label>
              <Input
                value={connectorConfig.org || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, org: e.target.value })}
                placeholder="Organization name"
              />
            </div>
          </>
        )}
        {connectorType === 's3' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>AWS Access Key</label>
              <Input.Password
                value={connectorConfig.aws_access_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, aws_access_key: e.target.value })}
                placeholder="AWS Access Key ID"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>AWS Secret Key</label>
              <Input.Password
                value={connectorConfig.aws_secret_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, aws_secret_key: e.target.value })}
                placeholder="AWS Secret Access Key"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Bucket Name</label>
              <Input
                value={connectorConfig.bucket_name || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, bucket_name: e.target.value })}
                placeholder="S3 bucket name"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Region</label>
              <Input
                value={connectorConfig.region || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, region: e.target.value })}
                placeholder="us-east-1"
              />
            </div>
          </>
        )}
        {connectorType === 'datadog' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>API Key</label>
              <Input.Password
                value={connectorConfig.api_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, api_key: e.target.value })}
                placeholder="Datadog API Key"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>App Key</label>
              <Input.Password
                value={connectorConfig.app_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, app_key: e.target.value })}
                placeholder="Datadog Application Key"
              />
            </div>
          </>
        )}
        {connectorType === 'aws_cloudtrail' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                AWS Access Key <span style={{ color: 'red' }}>*</span>
              </label>
              <Input.Password
                value={connectorConfig.aws_access_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, aws_access_key: e.target.value })}
                placeholder="AKIA..."
                required
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                AWS Secret Key <span style={{ color: 'red' }}>*</span>
              </label>
              <Input.Password
                value={connectorConfig.aws_secret_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, aws_secret_key: e.target.value })}
                placeholder="Enter AWS Secret Access Key"
                required
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Region <span style={{ color: 'red' }}>*</span>
              </label>
              <Input
                value={connectorConfig.region || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, region: e.target.value })}
                placeholder="us-east-1"
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Trail Name (optional)</label>
              <Input
                value={connectorConfig.trail_name || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, trail_name: e.target.value })}
                placeholder="Leave empty for all trails"
              />
            </div>
          </>
        )}
        {connectorType === 'aws_config' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>AWS Access Key</label>
              <Input.Password
                value={connectorConfig.aws_access_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, aws_access_key: e.target.value })}
                placeholder="AWS Access Key ID"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>AWS Secret Key</label>
              <Input.Password
                value={connectorConfig.aws_secret_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, aws_secret_key: e.target.value })}
                placeholder="AWS Secret Access Key"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Region</label>
              <Input
                value={connectorConfig.region || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, region: e.target.value })}
                placeholder="us-east-1"
              />
            </div>
          </>
        )}
        {connectorType === 'jira' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Site URL</label>
              <Input
                value={connectorConfig.site_url || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, site_url: e.target.value })}
                placeholder="company.atlassian.net"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Email</label>
              <Input
                value={connectorConfig.email || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, email: e.target.value })}
                placeholder="user@example.com"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>API Token</label>
              <Input.Password
                value={connectorConfig.api_token || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, api_token: e.target.value })}
                placeholder="Jira API Token"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Webhook Secret (optional)</label>
              <Input.Password
                value={connectorConfig.webhook_secret || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, webhook_secret: e.target.value })}
                placeholder="For webhook signature validation"
              />
            </div>
          </>
        )}
        {connectorType === 'slack' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Bot Token</label>
              <Input.Password
                value={connectorConfig.bot_token || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, bot_token: e.target.value })}
                placeholder="xoxb-..."
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Signing Secret</label>
              <Input.Password
                value={connectorConfig.signing_secret || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, signing_secret: e.target.value })}
                placeholder="For webhook signature validation"
              />
            </div>
          </>
        )}
        {connectorType === 'mlflow' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Tracking URI</label>
              <Input
                value={connectorConfig.tracking_uri || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, tracking_uri: e.target.value })}
                placeholder="http://localhost:5000"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Username (optional)</label>
              <Input
                value={connectorConfig.username || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, username: e.target.value })}
                placeholder="If auth enabled"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Password (optional)</label>
              <Input.Password
                value={connectorConfig.password || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, password: e.target.value })}
                placeholder="If auth enabled"
              />
            </div>
          </>
        )}
        {connectorType === 'openai' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>API Key</label>
              <Input.Password
                value={connectorConfig.api_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, api_key: e.target.value })}
                placeholder="sk-..."
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Organization ID (optional)</label>
              <Input
                value={connectorConfig.organization_id || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, organization_id: e.target.value })}
                placeholder="org-..."
              />
            </div>
          </>
        )}
        {connectorType === 'bedrock' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>AWS Access Key</label>
              <Input.Password
                value={connectorConfig.aws_access_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, aws_access_key: e.target.value })}
                placeholder="AWS Access Key ID"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>AWS Secret Key</label>
              <Input.Password
                value={connectorConfig.aws_secret_key || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, aws_secret_key: e.target.value })}
                placeholder="AWS Secret Access Key"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Region</label>
              <Input
                value={connectorConfig.region || ''}
                onChange={(e) => setConnectorConfig({ ...connectorConfig, region: e.target.value })}
                placeholder="us-east-1"
              />
            </div>
          </>
        )}

        {/* Initial Sync Toggle - Only show for new connectors */}
        {!editMode && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            background: '#f0f2f5',
            borderRadius: '4px',
            border: '1px solid #d9d9d9'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontWeight: 'bold', marginBottom: '4px', display: 'block' }}>
                  Initial Sync
                </label>
                <div style={{ color: '#666', fontSize: '12px' }}>
                  Automatically sync data from this connector after creation
                </div>
              </div>
              <Switch
                checked={initialSync}
                onChange={setInitialSync}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </div>
          </div>
        )}
      </Modal>

      <CopyConnectorModal
        open={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        connectors={connectorsToCopy}
        currentProjectId={projectId}
        onSuccess={handleCopySuccess}
      />
    </TrustGridPage>
  );
}

export default Connectors;
